import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useScanContext } from '../contexts/ScanContext';
import ReportsDashboard from './ReportsDashboard';
import api from '../services/api';
import { formatDate } from '../utils/dateUtils';
import { generateSecOpsTemplate } from '../utils/securityUtils';
import TGChibi, { TGChibiVariants } from '../components/TGChibi';
import {
  DocumentArrowDownIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  GlobeAltIcon,
  CalendarIcon,
  FunnelIcon,
  TagIcon,
  PencilIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

export default function Reports() {
  const { scanId } = useParams();
  const { refreshTrigger, refreshScanData, refreshWhitelistData } = useScanContext();
  const [scan, setScan] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [sortedFindings, setSortedFindings] = useState([]);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [tagForm, setTagForm] = useState({
    status: '',
    note: '',
    addToWhitelist: false,
    whitelistType: 'domain',
    whitelistReason: ''
  });
  const [sortBy, setSortBy] = useState('severity');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [showTagModal, setShowTagModal] = useState(false);
  const [refreshIndicator, setRefreshIndicator] = useState(false);
  const [bulkActionsExpanded, setBulkActionsExpanded] = useState(false);

  // Grouping State
  const [expandedGroups, setExpandedGroups] = useState({});

  // Copy SecOps Email State
  const [copiedId, setCopiedId] = useState(null);

  const handleCopySecOpsEmail = (finding) => {
    const targetDomain = scan?.domain?.domain_name || 'Target';
    const emailText = generateSecOpsTemplate(finding, targetDomain);
    
    navigator.clipboard.writeText(emailText).then(() => {
      setCopiedId(finding.id || finding.typosquat_domain);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  // Dynamic Grouping Logic
  const groupedDisplayFindings = useMemo(() => {
    if (!findings || findings.length === 0) return {};

    const groups = {};

    findings.forEach(finding => {
      let key = 'Unknown';

      if (sortBy === 'severity') {
        key = finding.severity?.value || finding.severity || 'Unknown';
      } else if (sortBy === 'registrar') {
        key = (finding.registrar === '?' || !finding.registrar) ? 'Unknown Registrar' : finding.registrar;
      } else if (sortBy === 'creation_date') {
        key = (finding.creation_date === '?' || !finding.creation_date) ? 'Unknown Date' : finding.creation_date;
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(finding);
    });

    return groups;
  }, [findings, sortBy]);

  // Keys sorted based on criteria
  const sortedGroupKeys = useMemo(() => {
    const keys = Object.keys(groupedDisplayFindings);

    if (sortBy === 'severity') {
      const order = { 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3, 'WHITELISTED': 4 };
      keys.sort((a, b) => (order[a] || 99) - (order[b] || 99));
    } else if (sortBy === 'creation_date') {
      keys.sort().reverse(); // Newest (likely ISO strings) first
    } else {
      // Registrar: Sort by count desc, then alpha
      keys.sort((a, b) => {
        const countDiff = groupedDisplayFindings[b].length - groupedDisplayFindings[a].length;
        if (countDiff !== 0) return countDiff;
        return a.localeCompare(b);
      });
    }

    return keys;
  }, [groupedDisplayFindings, sortBy]);

  // Initialize expanded state when keys change
  useEffect(() => {
    const initial = {};
    sortedGroupKeys.forEach(key => initial[key] = true);
    setExpandedGroups(prev => ({ ...initial, ...prev })); // Keep user toggles if keys exist
  }, [sortedGroupKeys]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const expandAll = () => {
    const all = {};
    sortedGroupKeys.forEach(key => all[key] = true);
    setExpandedGroups(all);
  };

  const collapseAll = () => {
    const all = {};
    sortedGroupKeys.forEach(key => all[key] = false);
    setExpandedGroups(all);
  };

  // Group findings by severity for summary cards
  const groupedFindings = useMemo(() => {
    if (!findings || findings.length === 0) {
      return { HIGH: [], MEDIUM: [], LOW: [], WHITELISTED: [], high: [], medium: [], low: [] };
    }

    const high = findings.filter(f => f.severity?.value === 'HIGH' || f.severity === 'HIGH');
    const medium = findings.filter(f => f.severity?.value === 'MEDIUM' || f.severity === 'MEDIUM');
    const low = findings.filter(f => f.severity?.value === 'LOW' || f.severity === 'LOW');
    const whitelisted = findings.filter(f => f.severity?.value === 'WHITELISTED' || f.severity === 'WHITELISTED');

    return {
      // Uppercase keys for display code
      HIGH: high,
      MEDIUM: medium,
      LOW: low,
      WHITELISTED: whitelisted,
      // Lowercase keys for summary cards
      high: high,
      medium: medium,
      low: low
    };
  }, [findings]);

  // Status options for tagging
  const statusOptions = [
    { value: 'to_review', label: 'To Review', color: 'bg-gray-100 text-gray-800 border-gray-300' },
    { value: 'confirmed_threat', label: 'Confirmed Threat', color: 'bg-red-100 text-red-800 border-red-300' },
    { value: 'not_a_threat', label: 'Not a Threat', color: 'bg-green-100 text-green-800 border-green-300' },
    { value: 'ignore_always', label: 'Ignore Always', color: 'bg-gray-100 text-gray-600 border-gray-300' },
    { value: 'controlled_domain', label: 'Controlled Domain', color: 'bg-gray-100 text-gray-800 border-gray-300' }
  ];

  const getStatusDisplay = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status) || statusOptions[0];
    return statusConfig;
  };

  const handleTagFinding = async (findingId) => {
    setSelectedFinding(findingId);
    const finding = findings.find(f => f.id === findingId);
    setTagForm({
      status: finding?.status || 'to_review',
      note: finding?.user_note || '',
      addToWhitelist: false,
      whitelistType: 'domain',
      whitelistReason: ''
    });
  };

  const handleSubmitTag = async (e) => {
    e.preventDefault();

    try {
      const response = await api.put(
        `/tags/findings/${selectedFinding}`,
        {
          status: tagForm.status,
          user_note: tagForm.note,
          addToWhitelist: tagForm.addToWhitelist,
          whitelistType: tagForm.whitelistType,
          whitelistReason: tagForm.whitelistReason
        }
      );

      // Update the finding in the local state
      setFindings(prev => prev.map(f =>
        f.id === selectedFinding
          ? { ...f, status: tagForm.status, user_note: tagForm.note, tagged_at: new Date().toISOString() }
          : f
      ));

      // Trigger refresh for both scan and whitelist data
      refreshScanData(scanId, 'tag-update');
      if (tagForm.addToWhitelist) {
        refreshWhitelistData('tag-update');
      }

      setSelectedFinding(null);
      setTagForm({ status: '', note: '', addToWhitelist: false, whitelistType: 'domain', whitelistReason: '' });
    } catch (error) {
      console.error('Error updating finding tag:', error);
      alert('Error updating tag. Please try again.');
    }
  };

  const handleCancelTag = () => {
    setSelectedFinding(null);
    setTagForm({ status: '', note: '', addToWhitelist: false, whitelistType: 'domain', whitelistReason: '' });
  };

  const handleBulkTag = async (status, addToWhitelist, filterFn = null) => {
    if (bulkProcessing) return;

    setBulkProcessing(true);
    setBulkResults(null);

    try {
      // Get findings to process based on filter function or default to untagged findings
      let findingsToProcess;

      if (filterFn) {
        findingsToProcess = findings.filter(filterFn);
      } else {
        findingsToProcess = findings.filter(finding =>
          finding.status === 'to_review' || !finding.status
        );
      }

      if (findingsToProcess.length === 0) {
        alert('No findings to process.');
        setBulkProcessing(false);
        return;
      }

      const confirmMessage = `This will tag ${findingsToProcess.length} findings as "${status.replace('_', ' ')}"${addToWhitelist ? ' and add them to whitelist' : ''}. Continue?`;

      if (!window.confirm(confirmMessage)) {
        setBulkProcessing(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process findings in batches to avoid overwhelming the server
      for (const finding of findingsToProcess) {
        try {
          const updateData = {
            status: status,
            user_note: `Bulk tagged as ${status.replace('_', ' ')}`,
            addToWhitelist: addToWhitelist,
            whitelistType: 'domain',
            whitelistReason: addToWhitelist ? `Bulk whitelisted from scan results - ${status.replace('_', ' ')}` : undefined
          };

          const response = await api.put(
            `/tags/findings/${finding.id}`,
            updateData
          );

          if (response.status === 200) {
            successCount++;
            // Update the finding in the local state
            setFindings(prev => prev.map(f =>
              f.id === finding.id
                ? { ...f, status: status, user_note: updateData.user_note, tagged_at: new Date().toISOString() }
                : f
            ));
          }
        } catch (error) {
          errorCount++;
          errors.push(`${finding.typosquat_domain}: ${error.response?.data?.detail || error.message}`);
        }

        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setBulkResults({
        total: findingsToProcess.length,
        success: successCount,
        errors: errorCount,
        errorDetails: errors
      });

      // Trigger refresh for both scan and whitelist data after bulk operations
      refreshScanData(scanId, 'bulk-tag');
      if (addToWhitelist && successCount > 0) {
        refreshWhitelistData('bulk-tag');
      }

    } catch (error) {
      console.error('Bulk tagging error:', error);
      alert('An error occurred during bulk tagging. Please try again.');
    } finally {
      setBulkProcessing(false);
    }
  };

  // Helper function to check if a finding has the redirect_to_root tag
  const hasRedirectToRootTag = (finding) => {
    // Check in finding.metadata.special_tags
    if (finding.metadata && finding.metadata.special_tags && finding.metadata.special_tags.includes('redirect_to_root')) {
      return true;
    }

    // Check in finding.finding_metadata.special_tags
    if (finding.finding_metadata && finding.finding_metadata.special_tags && finding.finding_metadata.special_tags.includes('redirect_to_root')) {
      return true;
    }

    // Check in finding.finding_metadata.http_check.redirects_to_root
    if (finding.finding_metadata && finding.finding_metadata.http_check && finding.finding_metadata.http_check.redirects_to_root === true) {
      return true;
    }

    // Check in finding.finding_metadata.metadata.special_tags (nested metadata)
    if (finding.finding_metadata && finding.finding_metadata.metadata &&
      finding.finding_metadata.metadata.special_tags &&
      finding.finding_metadata.metadata.special_tags.includes('redirect_to_root')) {
      return true;
    }

    // Check directly in finding.special_tags
    if (finding.special_tags && finding.special_tags.includes('redirect_to_root')) {
      return true;
    }

    return false;
  };

  const handleBulkUntag = async () => {
    if (bulkProcessing) return;

    setBulkProcessing(true);
    setBulkResults(null);

    try {
      // Get all tagged findings (not 'to_review' or empty status)
      const taggedFindings = findings.filter(finding =>
        finding.status && finding.status !== 'to_review'
      );

      if (taggedFindings.length === 0) {
        alert('No tagged findings to untag.');
        setBulkProcessing(false);
        return;
      }

      const confirmMessage = `This will remove tags from ${taggedFindings.length} findings, resetting them to "To Review" status. Continue?`;

      if (!window.confirm(confirmMessage)) {
        setBulkProcessing(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      // Process findings in batches to avoid overwhelming the server
      for (const finding of taggedFindings) {
        try {
          const updateData = {
            status: 'to_review',
            user_note: null,
            addToWhitelist: false
          };

          const response = await api.put(
            `/tags/findings/${finding.id}`,
            updateData
          );

          if (response.status === 200) {
            successCount++;
            // Update the finding in the local state
            setFindings(prev => prev.map(f =>
              f.id === finding.id
                ? { ...f, status: 'to_review', user_note: null, tagged_at: new Date().toISOString() }
                : f
            ));
          }
        } catch (error) {
          errorCount++;
          errors.push(`${finding.typosquat_domain}: ${error.response?.data?.detail || error.message}`);
        }

        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setBulkResults({
        total: taggedFindings.length,
        success: successCount,
        errors: errorCount,
        errorDetails: errors
      });

      // Trigger refresh for scan data after bulk operations
      refreshScanData(scanId, 'bulk-untag');

    } catch (error) {
      console.error('Bulk untagging error:', error);
      alert('An error occurred during bulk untagging. Please try again.');
    } finally {
      setBulkProcessing(false);
    }
  };

  useEffect(() => {
    console.log('Initial state - findings:', findings.length, 'sortedFindings:', sortedFindings.length, 'sortBy:', sortBy);
  }, []);

  useEffect(() => {
    if (scanId) {
      // Validate UUID format before fetching
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(scanId)) {
        setError('Invalid Scan ID format. Please ensure you have a valid link.');
        setLoading(false);
        return;
      }
      fetchScanData();
    } else {
      fetchAnalyticsData();
    }
  }, [scanId, refreshTrigger]);

  useEffect(() => {
    // Apply sorting whenever findings or sortBy changes
    console.log('useEffect triggered - findings count:', findings.length, 'sortBy:', sortBy);
    if (findings.length > 0) {
      console.log('Calling sortFindings with:', sortBy);
      sortFindings(sortBy);
    }
  }, [findings, sortBy]);

  useEffect(() => {
    const handleFocus = () => {
      console.log('Window focused - refreshing scan data');
      if (scanId) {
        fetchScanData();
      } else {
        fetchAnalyticsData();
      }
      setRefreshIndicator(true);
      setTimeout(() => setRefreshIndicator(false), 2000);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [scanId]);

  const fetchScanData = async () => {
    try {
      const [scanResponse, findingsResponse] = await Promise.all([
        api.get(`/scans/${scanId}`),
        api.get(`/scans/${scanId}/findings`)
      ]);

      setScan(scanResponse.data);
      setFindings(findingsResponse.data);

      // Sorting will be handled by useEffect when findings state updates
    } catch (error) {
      console.error('Failed to fetch scan data:', error);
      setError('Failed to load scan data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      const [summaryResponse, scansResponse] = await Promise.all([
        api.get('/scans/summary'),
        api.get('/scans/?limit=10')
      ]);

      setAnalytics(summaryResponse.data);
      setRecentScans(scansResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'HIGH':
        return 'text-red-600 bg-red-100 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800';
      case 'MEDIUM':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800';
      case 'LOW':
        return 'text-green-600 bg-green-100 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800';
      case 'WHITELISTED':
        return 'text-gray-600 bg-gray-100 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200 dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'HIGH':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      case 'MEDIUM':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'LOW':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'WHITELISTED':
        return <CheckCircleIcon className="h-5 w-5 text-gray-500" />;
      default:
        return <GlobeAltIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const downloadReport = async (format) => {
    try {
      const response = await api.get(`/reports/${scanId}/${format}`);

      if (format === 'html') {
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `typoguard-report-${scanId}.html`;
        link.click();
        window.URL.revokeObjectURL(url);
      } else {
        const json = JSON.stringify(response.data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `typoguard-report-${scanId}.json`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download report:', error);
      setError('Failed to download report');
    }
  };

  const sortFindings = (criteria) => {
    console.log('sortFindings called with criteria:', criteria, 'findings count:', findings.length);
    let sorted = [...findings];

    switch (criteria) {
      case 'severity':
        // Sort by risk_score descending (high risk first)
        sorted.sort((a, b) => {
          const scoreA = a.risk_score || 0;
          const scoreB = b.risk_score || 0;
          console.log(`Comparing ${a.typosquat_domain} (${scoreA}) vs ${b.typosquat_domain} (${scoreB})`);
          return scoreB - scoreA; // Descending order
        });
        console.log('Sorted by severity:', sorted.map(f => `${f.typosquat_domain}: ${f.risk_score}`));
        setSortedFindings(sorted);
        break;

      case 'creation_date':
        sorted.sort((a, b) => {
          const dateA = (a.creation_date === '?' || !a.creation_date) ? '0000-01-01' : a.creation_date;
          const dateB = (b.creation_date === '?' || !b.creation_date) ? '0000-01-01' : b.creation_date;
          
          const timeA = new Date(dateA).getTime();
          const timeB = new Date(dateB).getTime();
          
          return timeB - timeA; // Newest first
        });
        console.log('Sorted by creation date');
        setSortedFindings(sorted);
        break;

      case 'registrar':
        sorted.sort((a, b) => {
          const regA = (a.registrar === '?' || !a.registrar) ? 'unknown' : a.registrar.toLowerCase();
          const regB = (b.registrar === '?' || !b.registrar) ? 'unknown' : b.registrar.toLowerCase();
          return regA.localeCompare(regB);
        });
        console.log('Sorted by registrar');
        setSortedFindings(sorted);
        break;

      default:
        console.log('Default case - no sorting applied');
        setSortedFindings(findings);
    }
  };

  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <TGChibiVariants.Loading message="Loading scan report..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Error</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <div className="mt-6">
            <Link
              to="/scans"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700"
            >
              <ArrowLeftIcon className="-ml-1 mr-2 h-5 w-5" />
              Back to Scans
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!scanId) {
    return (
      <ReportsDashboard globalAnalytics={analytics} recentScans={recentScans} />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div>
              <Link
                to="/scans"
                className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 mb-2"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to Scans
              </Link>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Scan Report
              </h1>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {scan?.domain?.domain_name} • {scan ? formatDate(scan.started_at) : ''}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => downloadReport('html')}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 dark:border-dark-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600"
              >
                <DocumentArrowDownIcon className="-ml-1 mr-1.5 h-4 w-4" />
                Download HTML
              </button>
              <button
                onClick={() => downloadReport('json')}
                className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-mint-600 hover:bg-mint-700"
              >
                <DocumentArrowDownIcon className="-ml-1 mr-1.5 h-4 w-4" />
                Download JSON
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Panel */}
        {findings && findings.length > 0 && (
          <div className="mt-6 bg-white dark:bg-dark-800 shadow-sm border border-gray-200 dark:border-dark-600 rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-600 cursor-pointer"
              onClick={() => setBulkActionsExpanded(!bulkActionsExpanded)}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                    <svg className="h-5 w-5 mr-2 text-mint-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Smart Bulk Actions
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Efficiently tag multiple findings and create whitelist rules
                  </p>
                </div>
                <div className="flex items-center">
                  {bulkProcessing && (
                    <div className="flex items-center text-mint-600 mr-3">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                      <span className="text-sm font-medium">Processing...</span>
                    </div>
                  )}
                  <svg className={`h-5 w-5 text-gray-400 transition-transform ${bulkActionsExpanded ? 'transform rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Bulk Results Display */}
            {bulkResults && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-dark-700 border-b border-gray-200 dark:border-dark-600">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {bulkResults.errors === 0 ? (
                      <div className="flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 dark:bg-yellow-900/20 rounded-full">
                        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Bulk Operation Complete
                    </h4>
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      <span className="font-medium text-green-600 dark:text-green-400">{bulkResults.success} successful</span>
                      {bulkResults.errors > 0 && (
                        <span>, <span className="font-medium text-red-600 dark:text-red-400">{bulkResults.errors} failed</span></span>
                      )}
                      <span> out of {bulkResults.total} total</span>
                    </div>
                    {bulkResults.errors > 0 && bulkResults.errorDetails.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                          View error details
                        </summary>
                        <div className="mt-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/10 p-2 rounded border">
                          {bulkResults.errorDetails.slice(0, 5).map((error, index) => (
                            <div key={index}>{error}</div>
                          ))}
                          {bulkResults.errorDetails.length > 5 && (
                            <div>... and {bulkResults.errorDetails.length - 5} more errors</div>
                          )}
                        </div>
                      </details>
                    )}
                  </div>
                  <button
                    onClick={() => setBulkResults(null)}
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {bulkActionsExpanded && (
              <div className="px-6 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Quick Actions */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                      Quick Actions
                    </h4>
                    <div className="space-y-3">
                      <button
                        onClick={() => handleBulkTag('not_a_threat', true)}
                        disabled={bulkProcessing}
                        className="w-full group relative overflow-hidden rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4 text-left transition-all hover:bg-green-100 dark:hover:bg-green-900/30 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-green-100 dark:bg-green-800/50 rounded-full">
                                <svg className="w-4 h-4 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-green-800 dark:text-green-300">
                                Mark All as Safe
                              </div>
                              <div className="text-xs text-green-600 dark:text-green-400">
                                Tag untagged findings as "Not a Threat" + Auto-whitelist
                              </div>
                            </div>
                          </div>
                          <div className="text-xs bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
                            {findings.filter(f => f.status === 'to_review' || !f.status).length} items
                          </div>
                        </div>
                      </button>



                      <button
                        onClick={() => handleBulkTag('controlled_domain', true)}
                        disabled={bulkProcessing}
                        className="w-full group relative overflow-hidden rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700/50 p-4 text-left transition-all hover:bg-gray-50 dark:hover:bg-dark-600 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-dark-600 rounded-full">
                                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                Mark All as Controlled
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Tag untagged findings as "Controlled Domain" + Auto-whitelist
                              </div>
                            </div>
                          </div>
                          <div className="text-xs bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full border border-gray-200 dark:border-dark-600">
                            {findings.filter(f => f.status === 'to_review' || !f.status).length} items
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => handleBulkTag('ignore_always', true)}
                        disabled={bulkProcessing}
                        className="w-full group relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 p-4 text-left transition-all hover:bg-gray-100 dark:hover:bg-gray-600/50 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-600 rounded-full">
                                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                                </svg>
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-800 dark:text-gray-300">
                                Ignore All Forever
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">
                                Tag untagged findings as "Ignore Always" + Auto-whitelist
                              </div>
                            </div>
                          </div>
                          <div className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 px-2 py-1 rounded-full">
                            {findings.filter(f => f.status === 'to_review' || !f.status).length} items
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => handleBulkTag('controlled_domain', true, hasRedirectToRootTag)}
                        disabled={bulkProcessing}
                        className="w-full group relative overflow-hidden rounded-lg border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700/50 p-4 text-left transition-all hover:bg-gray-50 dark:hover:bg-dark-600 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-dark-600 rounded-full">
                                <svg className="w-4 h-4 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                Mark Redirects as Controlled
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Tag all "Redirects to root domain" findings as Controlled + Auto-whitelist
                              </div>
                            </div>
                          </div>
                          <div className="text-xs bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full border border-gray-200 dark:border-dark-600">
                            {findings.filter(hasRedirectToRootTag).length} items
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => handleBulkUntag()}
                        disabled={bulkProcessing}
                        className="w-full group relative overflow-hidden rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-left transition-all hover:bg-red-100 dark:hover:bg-red-900/30 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-red-100 dark:bg-red-800/50 rounded-full">
                                <svg className="w-4 h-4 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-red-800 dark:text-red-300">
                                Remove All Tags
                              </div>
                              <div className="text-xs text-red-600 dark:text-red-400">
                                Reset all tagged findings to "To Review" status
                              </div>
                            </div>
                          </div>
                          <div className="text-xs bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-1 rounded-full">
                            {findings.filter(f => f.status && f.status !== 'to_review').length} items
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Quick Link to Whitelist Management */}
                  <div className="pt-4 border-t border-gray-200 dark:border-dark-600">
                    <Link
                      to="/whitelist"
                      className="w-full inline-flex items-center justify-center px-4 py-3 border border-mint-200 dark:border-mint-800 text-sm font-medium rounded-lg text-mint-700 dark:text-mint-300 bg-mint-50 dark:bg-mint-900/20 hover:bg-mint-100 dark:hover:bg-mint-900/30 transition-colors"
                    >
                      <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543.94 3.31-.826 2.37-2.37a1.724 1.724 0 001.065-2.572c1.756-.426 1.756-2.924 0-3.35a1.724 1.724 0 00-1.066-2.573c-.426-1.756-2.924-1.756-3.35 0a1.724 1.724 0 00-2.573 1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c.426-1.756 2.924-1.756 3.35 0" />
                      </svg>
                      Manage Whitelist Rules
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Summary Cards */}
        {findings.length > 0 && (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-8 mt-8">
            <div className="bg-white dark:bg-dark-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <GlobeAltIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Total Findings
                      </dt>
                      <dd className="text-lg font-medium text-gray-900 dark:text-white">
                        {findings.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        High Risk
                      </dt>
                      <dd className="text-lg font-medium text-red-600 dark:text-red-400">
                        {groupedFindings.HIGH?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Medium Risk
                      </dt>
                      <dd className="text-lg font-medium text-yellow-600 dark:text-yellow-400">
                        {groupedFindings.MEDIUM?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-green-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Low Risk
                      </dt>
                      <dd className="text-lg font-medium text-green-600 dark:text-green-400">
                        {groupedFindings.LOW?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-dark-800 overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <CheckCircleIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                        Whitelisted
                      </dt>
                      <dd className="text-lg font-medium text-gray-600 dark:text-gray-400">
                        {groupedFindings.WHITELISTED?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grouping Controls */}
        {findings.length > 0 && (
          <div className="mb-6 bg-white dark:bg-dark-800 shadow rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <FunnelIcon className="h-5 w-5 text-gray-500" />
                  <label htmlFor="group-select" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Group by:
                  </label>
                </div>
                <select
                  id="group-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="block w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  <option value="registrar">Registrar</option>
                  <option value="severity">Severity</option>
                  <option value="creation_date">Creation Date</option>
                </select>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={expandAll}
                  className="text-sm text-mint-600 hover:text-mint-700 dark:text-mint-400 dark:hover:text-mint-300 font-medium"
                >
                  Expand All
                </button>
                <span className="text-gray-300 dark:text-gray-600">|</span>
                <button
                  onClick={collapseAll}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grouped Findings Display */}
        {sortedGroupKeys.map((groupKey) => {
          const groupFindings = groupedDisplayFindings[groupKey];
          if (!groupFindings || groupFindings.length === 0) return null;

          const isExpanded = expandedGroups[groupKey];

          return (
            <div key={groupKey} className="mb-6 bg-white dark:bg-dark-800 shadow rounded-lg overflow-hidden border border-transparent dark:border-dark-700">
              {/* Group Header - Clickable */}
              <div
                className={`px-4 py-4 sm:px-6 cursor-pointer transition-colors duration-150 flex items-center justify-between ${isExpanded ? 'bg-gray-50 dark:bg-dark-700/50' : 'hover:bg-gray-50 dark:hover:bg-dark-700/30'}`}
                onClick={() => toggleGroup(groupKey)}
              >
                <div className="flex items-center">
                  {isExpanded ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400 mr-3" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-3" />
                  )}

                  <div className="flex items-center">
                    {sortBy === 'severity' && getSeverityIcon(groupKey)}
                    <h3 className={`text-lg font-medium text-gray-900 dark:text-white ${sortBy === 'severity' ? 'ml-2 capitalize' : ''}`}>
                      {groupKey}
                    </h3>
                    <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {groupFindings.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Findings List (Collapsible) */}
              {isExpanded && (
                <div className="border-t border-gray-200 dark:border-dark-600">
                  <div className="p-4 space-y-4">
                    {groupFindings.map((finding, index) => (
                      <div key={finding.id || index} className="border border-gray-200 dark:border-dark-600 rounded-lg p-4 bg-white dark:bg-dark-800 hover:border-mint-200 dark:hover:border-mint-900/30 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-3">
                                <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                                  {finding.typosquat_domain}
                                </h4>
                                {/* Status Badge */}
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusDisplay(finding.status || 'to_review').color}`}>
                                  {getStatusDisplay(finding.status || 'to_review').label}
                                </span>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleCopySecOpsEmail(finding); }}
                                className="ml-4 inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 shadow-sm text-xs font-medium rounded text-gray-700 dark:text-gray-200 bg-white dark:bg-dark-700 hover:bg-gray-50 dark:hover:bg-dark-600 focus:outline-none transition-colors"
                              >
                                {copiedId === (finding.id || finding.typosquat_domain) ? (
                                  <>
                                    <CheckIcon className="h-4 w-4 mr-1.5 text-green-500" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <DocumentDuplicateIcon className="h-4 w-4 mr-1.5 text-gray-400 dark:text-gray-500" />
                                    Copy SecOps Email
                                  </>
                                )}
                              </button>
                            </div>

                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                              {finding.registrar && sortBy !== 'registrar' && (
                                <p><strong>Registrar:</strong> {finding.registrar === '?' ? 'Unknown' : finding.registrar}</p>
                              )}
                              {finding.creation_date && sortBy !== 'creation_date' && (
                                <p><strong>Created:</strong> {finding.creation_date === '?' ? 'Unknown' : finding.creation_date}</p>
                              )}
                              {finding.variant_type && (
                                <p><strong>Type:</strong> {finding.variant_type}</p>
                              )}
                              {finding.risk_level && (
                                <p><strong>Risk Level:</strong> {finding.risk_level}</p>
                              )}

                              {((finding.metadata && finding.metadata.special_tags && finding.metadata.special_tags.includes('redirect_to_root')) ||
                                (finding.finding_metadata && finding.finding_metadata.special_tags && finding.finding_metadata.special_tags.includes('redirect_to_root')) ||
                                (finding.finding_metadata && finding.finding_metadata.http_check && finding.finding_metadata.http_check.redirects_to_root === true) ||
                                (finding.finding_metadata && finding.finding_metadata.metadata &&
                                  finding.finding_metadata.metadata.special_tags &&
                                  finding.finding_metadata.metadata.special_tags.includes('redirect_to_root')) ||
                                (finding.special_tags && finding.special_tags.includes('redirect_to_root'))
                              ) && (
                                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                    <p className="text-sm text-blue-800 dark:text-blue-300 flex items-center">
                                      <span className="mr-1">↩️</span>
                                      <strong>Redirects to root domain</strong> - likely legitimate domain protection
                                    </p>
                                  </div>
                                )}

                              {finding.user_note && (
                                <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                                  <p className="text-sm text-blue-800 dark:text-blue-300">
                                    <strong>Note:</strong> {finding.user_note}
                                  </p>
                                </div>
                              )}
                              {finding.tagged_at && (
                                <p className="text-xs text-gray-500">
                                  <strong>Tagged:</strong> {formatDate(finding.tagged_at)}
                                </p>
                              )}
                            </div>

                            {finding.warning_message && (
                              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                                <div className="text-sm text-yellow-800 dark:text-yellow-300 whitespace-pre-line">
                                  {finding.warning_message}
                                </div>
                              </div>
                            )}

                            {/* Tagging Interface */}
                            {selectedFinding === finding.id ? (
                              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Status
                                    </label>
                                    <select
                                      value={tagForm.status}
                                      onChange={(e) => setTagForm({ ...tagForm, status: e.target.value })}
                                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                    >
                                      {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                          {option.label}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  {(tagForm.status === 'not_a_threat' || tagForm.status === 'controlled_domain' || tagForm.status === 'ignore_always') && (
                                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                                      <div className="flex items-start space-x-2">
                                        <div className="flex-shrink-0">
                                          <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                          </svg>
                                        </div>
                                        <div className="flex-1">
                                          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                                            Smart Whitelist Suggestion
                                          </h4>
                                          <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                                            Add this domain to your whitelist to prevent it from appearing in future scans?
                                          </p>
                                          <div className="mt-2 space-y-2">
                                            <label className="flex items-center">
                                              <input
                                                type="checkbox"
                                                checked={tagForm.addToWhitelist || false}
                                                onChange={(e) => setTagForm({ ...tagForm, addToWhitelist: e.target.checked })}
                                                className="h-4 w-4 text-mint-600 focus:ring-mint-500 border-gray-300 rounded"
                                              />
                                              <span className="ml-2 text-sm text-blue-700 dark:text-blue-400">
                                                Add to whitelist for future scans
                                              </span>
                                            </label>

                                            {tagForm.addToWhitelist && (
                                              <div className="ml-6 space-y-2">
                                                <div>
                                                  <label className="block text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">
                                                    Whitelist Type
                                                  </label>
                                                  <select
                                                    value={tagForm.whitelistType || 'domain'}
                                                    onChange={(e) => setTagForm({ ...tagForm, whitelistType: e.target.value })}
                                                    className="block w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded focus:outline-none focus:ring-1 focus:ring-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                  >
                                                    <option value="domain">Exact domain ({finding.typosquat_domain})</option>
                                                    <option value="subdomain">All subdomains (*.{finding.typosquat_domain.split('.').slice(-2).join('.')})</option>
                                                    <option value="pattern">Pattern matching</option>
                                                  </select>
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium text-blue-700 dark:text-blue-400 mb-1">
                                                    Whitelist Reason
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={tagForm.whitelistReason || ''}
                                                    onChange={(e) => setTagForm({ ...tagForm, whitelistReason: e.target.value })}
                                                    placeholder="e.g., Legitimate business partner"
                                                    className="block w-full px-2 py-1 text-xs border border-blue-300 dark:border-blue-600 rounded focus:outline-none focus:ring-1 focus:ring-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                  />
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Note (optional)
                                    </label>
                                    <textarea
                                      value={tagForm.note}
                                      onChange={(e) => setTagForm({ ...tagForm, note: e.target.value })}
                                      rows={2}
                                      className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-mint-500 focus:border-mint-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                      placeholder="Add a note about this finding..."
                                    />
                                  </div>
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={handleSubmitTag}
                                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-mint-600 hover:bg-mint-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                                    >
                                      <CheckIcon className="h-4 w-4 mr-1" />
                                      {tagForm.addToWhitelist ? 'Save & Whitelist' : 'Save'}
                                    </button>
                                    <button
                                      onClick={handleCancelTag}
                                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                                    >
                                      <XMarkIcon className="h-4 w-4 mr-1" />
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-3">
                                <button
                                  onClick={() => handleTagFinding(finding.id)}
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mint-500"
                                >
                                  <TagIcon className="h-4 w-4 mr-1" />
                                  {finding.status && finding.status !== 'to_review' ? 'Edit Tag' : 'Add Tag'}
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-end space-y-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getSeverityColor(finding.severity?.value || finding.severity || 'UNKNOWN')}`}>
                              {(finding.severity?.value || finding.severity || 'UNKNOWN').toUpperCase()}
                            </span>
                            {finding.risk_level && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {finding.risk_level} Risk
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {/* No Findings */}
        {findings.length === 0 && (
          <div className="bg-white dark:bg-dark-800 shadow rounded-lg">
            <div className="text-center py-12">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                No typosquatting domains found!
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Your domain appears to be safe from typosquatting attacks at this time.
              </p>
            </div>
          </div>
        )}

        {/* Scan Metadata */}
        <div className="mt-8 bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Scan Information
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Scan ID</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{scan?.scan_id}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Scan Type</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{scan?.scan_type}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white capitalize">{scan?.status}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Started</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                {scan ? formatDate(scan.started_at, true) : ''}
              </dd>
            </div>
            {scan?.completed_at && (
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                  {formatDate(scan.completed_at, true)}
                </dd>
              </div>
            )}
          </div>
        </div>
      </div>
      {
        refreshIndicator && (
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-green-100 dark:bg-green-900/20 border-t border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 text-center">
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
            </svg>
            <span className="text-sm font-medium">Refreshing data...</span>
          </div>
        )
      }
    </div >
  );
}
